import type { ConfigurationApplyCommand, ConfigurationApplyResult, ConfigurationSnapshot, EditorMetadata } from '../contracts'

export type ApplyIssue = { kind: 'unknown' | 'conflict' | 'failure'; message: string }
export type Draft = { base: ConfigurationSnapshot; path: string; values: Map<string, string>; created: Set<string>; removed: Set<string>
  metadata: EditorMetadata; issue: ApplyIssue | null; outcome: ConfigurationApplyResult | null; resultPath: string | null }
export type CatalogEdit = { kind: 'add_category'; key: string; title: string; directory?: string | null; icon_key?: string }
  | { kind: 'rename_category'; key: string; title: string; directory?: string | null; icon_key?: string } | { kind: 'delete_category'; key: string }
  | { kind: 'move_file'; path: string; category_key: string; before_path?: string }
  | { kind: 'rename_file'; path: string; label: string; icon_key?: string }

const metadataOf = (base: ConfigurationSnapshot): EditorMetadata => base.editor_metadata ?? {
  categories: [{ key: 'uncategorized', title: '未分类' }],
  entries: base.files.map((file) => ({ path: file.path, label: file.path, category_key: 'uncategorized' })), workflow_positions: {} }
const changedMetadata = (draft: Draft) => JSON.stringify(draft.metadata) !== JSON.stringify(metadataOf(draft.base))
export const draftOf = (base: ConfigurationSnapshot): Draft => ({ base,
  path: base.editor_metadata?.entries[0]?.path ?? base.files[0]?.path ?? '',
  values: new Map(), created: new Set(), removed: new Set(), metadata: metadataOf(base), issue: null, outcome: null, resultPath: null })
export const filesOf = (draft: Draft) => [...draft.base.files.filter((file) => !draft.removed.has(file.path)).map((file) => ({
  path: file.path, content: draft.values.get(file.path) ?? file.content })),
...Array.from(draft.created).map((path) => ({ path, content: draft.values.get(path) ?? '' }))]
export const replacements = (draft: Draft) => draft.base.files.filter((file) => !draft.removed.has(file.path)).flatMap((file) => {
  const content = draft.values.get(file.path)
  return content !== undefined && content !== file.content ? [{ path: file.path, expected_sha256: file.sha256, content }] : []
})
export const payloadOf = (draft: Draft): Omit<ConfigurationApplyCommand, 'request_id'> => {
  const creations = Array.from(draft.created).map((path) => ({ path, content: draft.values.get(path) ?? '' }))
  const deletions = draft.base.files.filter((file) => draft.removed.has(file.path)).map((file) => ({ path: file.path, expected_sha256: file.sha256 }))
  return { expected_active_revision: draft.base.active_revision, expected_editor_digest: draft.base.editor_digest,
    changes: replacements(draft), ...(creations.length ? { creations } : {}), ...(deletions.length ? { deletions } : {}),
    ...(changedMetadata(draft) ? { editor_metadata: draft.metadata } : {}) }
}
export const hasChanges = (draft: Draft) => replacements(draft).length > 0 || draft.created.size > 0
  || draft.base.files.some((file) => draft.removed.has(file.path)) || changedMetadata(draft)
/** File saves never own business-flow definitions, positions or activation. */
export const contentDraft = (draft: Draft, path = draft.path): Draft => ({
  ...draft, path, values: new Map([...draft.values].filter(([key]) => key === path)),
  created: new Set(), removed: new Set(), metadata: metadataOf(draft.base),
})

/** Install the accepted base atomically while retaining every still-unsubmitted content edit. */
const rebaseDraft = (draft: Draft, base: ConfigurationSnapshot): Draft => {
  const values = new Map([...draft.values].filter(([path, content]) => base.files.find((file) => file.path === path)?.content !== content))
  const created = new Set([...draft.created].filter((path) => !base.files.some((file) => file.path === path)))
  const previous = metadataOf(draft.base).workflow_positions
  const pending = Object.fromEntries(Object.entries(draft.metadata.workflow_positions).filter(([id, positions]) =>
    JSON.stringify(positions) !== JSON.stringify(previous[id])))
  const metadata = { ...metadataOf(base), workflow_positions: { ...metadataOf(base).workflow_positions, ...pending } }
  const next = { ...draft, base, values, created, metadata }
  return { ...next, path: filesOf(next).some((file) => file.path === next.path) ? next.path : filesOf(next)[0]?.path ?? '' }
}
export const acceptedDraft = (draft: Draft, outcome: ConfigurationApplyResult): Draft => ({
  ...rebaseDraft(draft, outcome.configuration), outcome, issue: null,
})

export const reloadDraft = (draft: Draft, base: ConfigurationSnapshot, path: string): Draft => {
  const values = new Map(draft.values); values.delete(path)
  const accepted = metadataOf(base).workflow_positions
  const positions = Object.fromEntries(Object.entries(draft.metadata.workflow_positions))
  const files = new Map(base.files.map((file) => [file.path, file]))
  const conflictingContent = [...values].some(([name, content]) => {
    const previous = draft.base.files.find((file) => file.path === name)
    const next = files.get(name)
    return content !== previous?.content && content !== next?.content && previous?.sha256 !== next?.sha256
  })
  const previousPositions = metadataOf(draft.base).workflow_positions
  const conflictingPositions = Object.entries(positions).some(([id, value]) =>
    JSON.stringify(value) !== JSON.stringify(previousPositions[id]) && JSON.stringify(value) !== JSON.stringify(accepted[id])
    && JSON.stringify(previousPositions[id]) !== JSON.stringify(accepted[id]))
  if (conflictingContent || conflictingPositions) return { ...draft, outcome: null,
    issue: { kind: 'conflict', message: '其他未保存内容已在服务端变化，请先处理这些修改' } }
  return { ...rebaseDraft({ ...draft, values, metadata: { ...draft.metadata,
    workflow_positions: { ...positions, ...accepted } } }, base),
  issue: null, outcome: null, resultPath: null }
}

export const createFile = (draft: Draft, name: string, content: string, label: string, category_key: string): Draft => {
  const category = draft.metadata.categories.find((item) => item.key === category_key)
  const directory = directoryOf(category?.directory)
  if (directory === undefined) throw new Error('请先配置分类目录')
  const path = directory === '.' ? name : directory + '/' + name
  if (!/\.(ya?ml|md)$/i.test(path) || Array.from(path).some((character) => character === '\\' || character === ':' || character.charCodeAt(0) < 32)
    || path.split('/').some((part) => !part || part === '.' || part === '..')) throw new Error('文件路径不可用')
  if (draft.base.files.some((file) => file.path === path) || draft.created.has(path)) throw new Error('文件已存在')
  if (!label.trim() || !draft.metadata.categories.some((category) => category.key === category_key)) throw new Error('请选择分类并填写名称')
  return { ...draft, path, values: new Map(draft.values).set(path, content), created: new Set([...draft.created, path]),
    metadata: { ...draft.metadata, entries: [...draft.metadata.entries, { path, label: label.trim(), category_key }] }, issue: null, outcome: null }
}

const directoryOf = (value?: string | null): string | undefined => {
  if (value == null || value === '') return undefined
  if (value === '.') return value
  if (value.split('/').some((part) => !part || part === '.' || part === '..')
    || Array.from(value).some((character) => character === '\\' || character === ':' || character.charCodeAt(0) < 32)) {
    throw new Error('分类目录不可用')
  }
  return value
}
export const deleteFile = (draft: Draft, path: string): Draft => {
  const created = new Set(draft.created); created.delete(path)
  const values = new Map(draft.values); values.delete(path)
  const next = { ...draft, created, values, removed: new Set([...draft.removed, path]),
    metadata: { ...draft.metadata, entries: draft.metadata.entries.filter((item) => item.path !== path) }, issue: null, outcome: null }
  return { ...next, path: filesOf(next).some((file) => file.path === next.path) ? next.path : filesOf(next)[0]?.path ?? '' }
}

const catalogEdits: Record<CatalogEdit['kind'], (metadata: EditorMetadata, command: CatalogEdit) => EditorMetadata> = {
  add_category: (metadata, command) => {
    if (command.kind !== 'add_category') return metadata
    if (!command.title.trim() || metadata.categories.some((item) => item.key === command.key)) throw new Error('分类名称为空或标识重复')
    const directory = directoryOf(command.directory)
    return { ...metadata, categories: [...metadata.categories, { key: command.key, title: command.title.trim(),
      ...(command.icon_key ? { icon_key: command.icon_key } : {}),
      ...(directory !== undefined ? { directory } : {}) }] }
  },
  rename_category: (metadata, command) => {
    if (command.kind !== 'rename_category') return metadata
    if (!command.title.trim()) throw new Error('请填写分类名称')
    const directory = directoryOf(command.directory)
    return { ...metadata, categories: metadata.categories.map((item) => item.key === command.key ? {
      ...item, title: command.title.trim(), ...('directory' in command ? { directory } : {}),
      ...('icon_key' in command ? { icon_key: command.icon_key } : {}),
    } : item) }
  },
  delete_category: (metadata, command) => {
    if (command.kind !== 'delete_category') return metadata
    if (command.key === 'uncategorized') throw new Error('未分类需要保留')
    return { ...metadata, categories: metadata.categories.filter((item) => item.key !== command.key),
      entries: metadata.entries.map((item) => item.category_key === command.key ? { ...item, category_key: 'uncategorized' } : item) }
  },
  rename_file: (metadata, command) => {
    if (command.kind !== 'rename_file') return metadata
    if (!command.label.trim()) throw new Error('请填写名称')
    return { ...metadata, entries: metadata.entries.map((item) => item.path === command.path ? { ...item, label: command.label.trim(),
      ...('icon_key' in command ? { icon_key: command.icon_key } : {}) } : item) }
  },
  move_file: (metadata, command) => {
    if (command.kind !== 'move_file') return metadata
    const source = metadata.entries.find((item) => item.path === command.path)
    if (!source || !metadata.categories.some((item) => item.key === command.category_key)) throw new Error('文件或分类已不存在')
    const rest = metadata.entries.filter((item) => item.path !== command.path)
    const index = rest.findIndex((item) => item.path === command.before_path)
    const position = index < 0 ? rest.length : index
    return { ...metadata, entries: [...rest.slice(0, position), { ...source, category_key: command.category_key }, ...rest.slice(position)] }
  },
}
export const editCatalog = (draft: Draft, command: CatalogEdit): Draft => ({ ...draft,
  metadata: catalogEdits[command.kind](draft.metadata, command), issue: null, outcome: null })
